import { useEffect, useState } from "react";
import { posthog } from "src/initialisePostHog";

// Reads a deterministic per-flag override from the URL query string, e.g.
// `?ff_profile-stats=true`. This lets e2e and local development pin a flag to a
// known state without depending on live PostHog network calls. Returns true/false
// for an explicit override, or null when no override is present for this key.
const readOverride = (flagKey: string): boolean | null => {
  if (typeof window === "undefined") return null;
  try {
    const value = new URLSearchParams(window.location.search).get(`ff_${flagKey}`);
    if (value === null) return null;
    return value === "true" || value === "1";
  } catch {
    return null;
  }
};

// Fail-closed read of a PostHog boolean flag: the flag is enabled ONLY when
// PostHog reports it as strictly `true`. Never throws — if PostHog is
// uninitialised (e.g. the test environment), still loading, or blocked by an
// ad-blocker, this resolves to false (OFF). A URL override takes precedence.
const readFlag = (flagKey: string): boolean => {
  const override = readOverride(flagKey);
  if (override !== null) return override;
  try {
    return posthog.isFeatureEnabled(flagKey) === true;
  } catch {
    return false;
  }
};

/**
 * Subscribe to a PostHog boolean feature flag, defaulting OFF.
 *
 * Fail-closed by design: the flag is treated as enabled only when PostHog
 * reports it as strictly `true`. Every other state — undefined, still-loading,
 * an error, an ad-blocked or uninitialised PostHog, or the test environment —
 * resolves to false. A `?ff_<key>=true|false` query-string override takes
 * precedence so e2e and local dev never depend on live PostHog network state.
 *
 * The value is reactive: PostHog calls back via `onFeatureFlags` once flags
 * load (or refresh), and the hook re-renders with the new value. The listener
 * is removed on unmount. On first paint (before flags resolve) the flag is OFF.
 */
export const useFeatureFlagEnabled = (flagKey: string): boolean => {
  const [enabled, setEnabled] = useState<boolean>(() => readFlag(flagKey));

  useEffect(() => {
    // Re-evaluate now in case flags resolved between the initial render and this
    // effect, then subscribe for subsequent flag loads/refreshes.
    setEnabled(readFlag(flagKey));

    let unsubscribe: (() => void) | undefined;
    try {
      unsubscribe = posthog.onFeatureFlags(() => setEnabled(readFlag(flagKey)));
    } catch {
      // PostHog uninitialised or blocked — remain fail-closed (already OFF).
    }
    return () => {
      try {
        unsubscribe?.();
      } catch {
        // Ignore teardown errors from an uninitialised/blocked PostHog.
      }
    };
  }, [flagKey]);

  return enabled;
};
